import { leaveLink } from '../claim/leaveLink';
import { numericAdaOrLeaveKeyFromClaim } from '../todo-claim-numeric-key';
import { FormatPipe } from '../../pipes/format.pipe';
import { formatDiaryNotificationTime } from './diary-notification-time';

export function normalizeDiaryRecordId(diary: any): string | number | null {
  const rid = diary?.recordID ?? diary?.recordId;
  if (rid == null || rid === '') {
    return null;
  }
  if (typeof rid === 'object' && rid !== null && 'value' in rid) {
    return (rid as { value: unknown }).value as string | number;
  }
  return rid as string | number;
}

export function getRecordSourceAndId(
  diary: any,
  claim?: any,
): { recordSource: string; recordId: any } {
  if (diary.claimUID) {
    return { recordSource: 'J', recordId: diary.claimUID };
  }
  const ln = diary?.leaveNumber;
  if (ln !== undefined && ln !== null && ln !== '') {
    return { recordSource: 'L', recordId: ln };
  }
  if (diary.absenceUID) {
    return {
      recordSource: 'T',
      recordId:
        diary.absenceUID ||
        diary.employeeUID ||
        normalizeDiaryRecordId(diary) ||
        claim?.recordId,
    };
  }
  const tamsReq = diary?.adatamsRequestID ?? diary?.adaTamsRequestID ?? null;
  if (tamsReq) {
    return { recordSource: 'R', recordId: tamsReq };
  }
  if (diary.adaEventNumber) {
    return { recordSource: 'A', recordId: diary.adaEventNumber };
  }
  if (diary.employeeUID) {
    return {
      recordSource: 'T',
      recordId:
        diary.employeeUID ||
        normalizeDiaryRecordId(diary) ||
        claim?.recordId,
    };
  }
  return { recordSource: '', recordId: null };
}

export function getRecordSourceDisplayInfo(
  recordSource: string,
  diary: any,
  recordId: any,
): { claimDisplayValue: string; fileNumber: string } {
  switch (recordSource) {
    case 'J':
      return {
        claimDisplayValue: diary.claimUID || '',
        fileNumber: diary.diaryFor || '',
      };
    case 'T':
      return {
        claimDisplayValue: recordId,
        fileNumber: diary.absenceUID || diary.employeeUID || '',
      };
    case 'A':
      return {
        claimDisplayValue: diary.adaEventNumber || '',
        fileNumber: diary.adaEventNumber || '',
      };
    case 'L':
      return {
        claimDisplayValue: diary.leaveNumber || '',
        fileNumber: diary.leaveNumber || '',
      };
    case 'R':
      return {
        claimDisplayValue: diary.adatamsEventNumber || '',
        fileNumber: diary.adatamsEventNumber || '',
      };
    default:
      return {
        claimDisplayValue: '',
        fileNumber: diary.diaryFor || '',
      };
  }
}

export function matchesCurrentClaim(
  diary: any,
  recordSource: string,
  recordId: any,
  claim: any,
): boolean {
  if (!claim || !claim.recordSource) {
    return false;
  }

  if (leaveLink(claim.recordSource) && recordSource === 'L') {
    const claimLeave = numericAdaOrLeaveKeyFromClaim(claim);
    const diaryLeave = numericAdaOrLeaveKeyFromClaim({
      fileNumber: diary?.leaveNumber ?? recordId,
    });
    return (
      claimLeave != null && diaryLeave != null && claimLeave === diaryLeave
    );
  }

  const displayInfo = getRecordSourceDisplayInfo(recordSource, diary, recordId);
  const diaryFileNumber = displayInfo.fileNumber;
  const claimFileNumber = claim.fileNumber;

  if (diaryFileNumber && claimFileNumber) {
    return diaryFileNumber === claimFileNumber;
  }

  const diaryRecordId = recordId?.toString();
  const claimRecordId = claim.recordId?.toString();

  return (
    recordSource === claim.recordSource && diaryRecordId === claimRecordId
  );
}

export function getDiaryStatusLabel(status: string, dueDate: Date): string {
  const now = new Date();
  const nowDateOnly = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const due = new Date(dueDate);
  const dueDateOnly = new Date(
    due.getFullYear(),
    due.getMonth(),
    due.getDate(),
  );
  const normalizedStatus = status?.toLocaleLowerCase();

  if (normalizedStatus === 'completed') return 'Completed';
  if (normalizedStatus === 'draft') return 'Draft';
  if (dueDateOnly < nowDateOnly && normalizedStatus !== 'completed')
    return 'Past due';
  return 'Open';
}

export function mapDiariesToTableRows(
  diaries: any[],
  formatPipe: FormatPipe,
  options: {
    isPageTodo: boolean;
    claim?: any;
    includeCompleted: boolean;
    isSearchView?: boolean;
  },
): any[] {
  const statusFiltered =
    options.includeCompleted || options.isSearchView
      ? diaries
      : diaries.filter(
          (diary: any) => (diary.status || '').toLowerCase() !== 'completed',
        );

  const located = statusFiltered.map((diary: any) => {
    const apiRecordId = normalizeDiaryRecordId(diary);
    const { recordSource, recordId } = getRecordSourceAndId(
      diary,
      options.claim,
    );
    return { diary, apiRecordId, recordSource, recordId };
  });

  const matched =
    !options.isPageTodo && options.claim
      ? located.filter((row) =>
          matchesCurrentClaim(
            row.diary,
            row.recordSource,
            row.recordId,
            options.claim,
          ),
        )
      : located;

  return matched.map((row) => {
    const diary = row.diary;
    const displayInfo = getRecordSourceDisplayInfo(
      row.recordSource,
      diary,
      row.recordId,
    );
    const notificationDate = formatDiaryNotificationTime(diary);

    return {
      id: diary.diaryUID,
      clientId: diary.clientNumber,
      case: displayInfo.claimDisplayValue,
      fileNumber: displayInfo.fileNumber,
      firstName: diary.firstName,
      lastName: diary.lastName,
      fullName:
        diary.lastName && diary.firstName
          ? `${diary.lastName}, ${diary.firstName}`
          : '',
      dueDate: diary.dateDue,
      notificationDate,
      formattedDueDate: formatPipe.transform(diary.dateDue, 'd'),
      formattedNotificationDate: notificationDate,
      diaryType: diary.typeDescription,
      diaryTypeId: diary.typeUID,
      formattedUsers: diary.additionalUsers
        ? `${diary.followUpUser} ${diary.additionalUsers
            .map((a: any) => a.userName)
            .join(' ')}`
        : diary.followUpUser,
      followUpUser: diary.followUpUser,
      additionalUsers: diary.additionalUsers,
      status: getDiaryStatusLabel(diary.status, diary.dateDue),
      message: diary.description,
      allowOthers: !diary.completeByCreator,
      emailReminder: diary.emailReminder,
      hours: diary.hours,
      minutes: diary.minutes,
      timeOffset: diary.timeOffset,
      notificationTime: diary.notificationTime,
      isCompleted: diary.status === 'Completed',
      createdBy: diary.createdBy,
      dateCreated: diary.dateCreated,
      updatedBy: diary.updatedBy,
      dateUpdated: diary.dateUpdated,
      recordSource: row.recordSource,
      recordId: row.recordId,
      navigatingRecID: row.apiRecordId ?? row.recordId,
      format: diary?.format,
    };
  });
}

export function getClaimSpecificFields(claim: any): any {
  const z = {
    claimUID: null as any,
    absenceUID: null as any,
    aDAEventNumber: null as any,
    leaveNumber: null as any,
    employeeUID: null as any,
  };
  if (!claim) {
    return {
      claimUID: null,
      absenceUID: null,
      aDAEventNumber: null,
      leaveNumber: null,
    };
  }
  switch (claim.recordSource) {
    case 'J':
      return { ...z, claimUID: claim.recordId };
    case 'T':
      return {
        ...z,
        absenceUID: claim.fileNumber,
        employeeUID: claim.hrMasterUid,
      };
    case 'A':
      return {
        ...z,
        aDAEventNumber: numericAdaOrLeaveKeyFromClaim(claim),
      };
    case 'L':
      return { ...z, leaveNumber: numericAdaOrLeaveKeyFromClaim(claim) };
    case 'R':
      return { ...z };
    default:
      return { ...z, claimUID: claim.recordId };
  }
}

export function filterDiariesByDate(diaries: any[], targetDate: Date): any[] {
  return diaries.filter((diary: any) => {
    const dueDate = new Date(diary.dateDue);
    return dueDate.toDateString() === targetDate.toDateString();
  });
}

export const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export function formatThreeMonthRange(anchor: Date): string {
  const start = new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1);
  const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1);
  return `${MONTHS_SHORT[start.getMonth()]} – ${MONTHS_SHORT[end.getMonth()]} ${end.getFullYear()}`;
}

export function getThreeMonthBounds(anchor: Date): { start: Date; end: Date } {
  const start = new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1);
  const end = new Date(anchor.getFullYear(), anchor.getMonth() + 2, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

export function getDefaultThreeMonthAnchor(): Date {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), 1);
}
